import { supabase } from "@/integrations/supabase/client";
import { KASHIER_CONFIG } from "@/config/kashier";
import { pb } from "@/lib/pocketbase";

export interface CreateKashierCheckoutData {
  bookingId: string;
  amount: number;
  customerEmail: string;
  customerName: string;
  tutorName: string;
  subject: string;
  date: string;
  time: string;
}

export interface KashierCheckoutResponse {
  success: boolean;
  merchantId?: string;
  orderId?: string;
  amount?: string | number;  // Can be decimal string or number
  currency?: string;
  hash?: string;
  customerEmail?: string;
  customerName?: string;
  description?: string;
  redirectUrl?: string;
  webhookUrl?: string;
  error?: string;
}

export const kashierService = {
  /**
   * Creates a Kashier checkout session
   */
  async createCheckoutSession(data: CreateKashierCheckoutData): Promise<KashierCheckoutResponse> {
    try {
      console.log('Creating Kashier checkout session:', data);

      // Get PocketBase auth token
      const pbToken = pb.authStore.token;
      if (!pbToken) {
        throw new Error('User not authenticated with PocketBase');
      }

      const { data: response, error } = await supabase.functions.invoke('create-kashier-checkout', {
        body: data,
        headers: {
          'x-pocketbase-auth': pbToken,
        },
      });

      if (error) {
        console.error('Error creating Kashier checkout:', error);
        throw error;
      }

      console.log('Kashier checkout session created:', response);
      return response as KashierCheckoutResponse;
    } catch (error) {
      console.error('Error in createCheckoutSession:', error);
      throw error;
    }
  },

  /**
   * Redirects to Kashier payment page
   */
  redirectToPayment(checkoutData: KashierCheckoutResponse) {
    if (!checkoutData.merchantId || !checkoutData.orderId || !checkoutData.hash) {
      console.error('Invalid checkout data');
      throw new Error('Invalid checkout data - missing required fields');
    }

    console.log('🔄 Redirecting to Kashier checkout...');
    console.log('📦 Checkout data:', checkoutData);

    try {
      // Build Kashier HPP URL with query parameters (as per their official demo)
      const params = new URLSearchParams({
        merchantId: checkoutData.merchantId,
        orderId: checkoutData.orderId,
        mode: KASHIER_CONFIG.TEST_MODE ? 'test' : 'live',
        amount: String(checkoutData.amount),
        currency: checkoutData.currency || 'EGP',
        hash: checkoutData.hash,
        display: 'en',
        allowedMethods: KASHIER_CONFIG.PAYMENT_METHODS.join(','),
      });

      // Add optional parameters
      if (checkoutData.redirectUrl) {
        params.set('merchantRedirect', encodeURIComponent(checkoutData.redirectUrl));
      }
      if (checkoutData.webhookUrl) {
        params.set('serverWebhook', checkoutData.webhookUrl);
      }
      if (checkoutData.description) {
        params.set('description', checkoutData.description);
      }

      const checkoutUrl = `${KASHIER_CONFIG.REDIRECT_URL}?${params.toString()}`;
      
      console.log('✅ Redirecting to:', checkoutUrl);

      // Redirect to Kashier HPP
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('❌ Error during redirect:', error);
      throw error;
    }
  },

  /**
   * Opens Kashier payment in an iframe popup
   */
  openIframePayment(checkoutData: KashierCheckoutResponse) {
    if (!checkoutData.merchantId || !checkoutData.orderId || !checkoutData.hash) {
      console.error('Invalid checkout data');
      throw new Error('Invalid checkout data - missing required fields');
    }

    console.log('🔄 Opening Kashier iframe payment...');
    console.log('📦 Checkout data:', checkoutData);

    try {
      // Remove any existing Kashier iframe script first
      const existingScript = document.getElementById('kashier-iFrame');
      if (existingScript) {
        console.log('🗑️ Removing existing Kashier script...');
        existingScript.remove();
        // Wait a bit for cleanup
        setTimeout(() => this.injectKashierScript(checkoutData), 100);
      } else {
        this.injectKashierScript(checkoutData);
      }
    } catch (error) {
      console.error('❌ Error opening iframe payment:', error);
      throw error;
    }
  },

  /**
   * Cleanup Kashier resources
   */
  cleanup() {
    // Remove Kashier script if exists
    const script = document.getElementById('kashier-iFrame');
    if (script) {
      console.log('🗑️ Cleaning up Kashier script...');
      script.remove();
    }

    // Remove event listener
    window.removeEventListener('message', this.messageHandler);
  },

  // Store message handler reference for cleanup
  messageHandler: null as ((e: MessageEvent) => void) | null,

  /**
   * Injects the Kashier iframe script into the DOM
   */
  injectKashierScript(checkoutData: KashierCheckoutResponse) {
    // Remove old event listener if exists
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
    }

    // Set up message event listener for iframe responses FIRST
    const responseCallback = (e: MessageEvent) => {
      console.log('📨 Received postMessage:', e.data);
      
      if (e.data.message === "success") {
        console.log("✅ Kashier payment success:", e.data);
        // Handle success - redirect to confirmation page
        if (checkoutData.redirectUrl) {
          window.location.href = checkoutData.redirectUrl;
        }
      } else if (e.data.message === "failure") {
        console.log("❌ Kashier payment failure:", e.data);
        this.cleanup();
      } else if (e.data.message === "cancel") {
        console.log("⚠️ Kashier payment canceled:", e.data);
        this.cleanup();
      } else {
        console.log("ℹ️ Kashier iframe event:", e.data);
      }
    };

    // Store handler reference for cleanup
    this.messageHandler = responseCallback;

    // Add event listener
    window.addEventListener("message", responseCallback, false);
    console.log('✅ PostMessage listener registered');

    // Create the Kashier iframe script element
    const script = document.createElement('script');
    script.id = 'kashier-iFrame';
    script.src = 'https://payments.kashier.io/kashier-checkout.js';
    
    // Set all required data attributes per Kashier documentation
    script.setAttribute('data-amount', String(checkoutData.amount));
    script.setAttribute('data-currency', checkoutData.currency || 'EGP');
    script.setAttribute('data-hash', checkoutData.hash);
    script.setAttribute('data-orderId', checkoutData.orderId);
    script.setAttribute('data-merchantId', checkoutData.merchantId);
    script.setAttribute('data-mode', KASHIER_CONFIG.TEST_MODE ? 'test' : 'live');
    script.setAttribute('data-display', 'en');
    script.setAttribute('data-type', 'external');  // Must be 'external' for iframe
    
    // Optional attributes
    if (checkoutData.redirectUrl) {
      script.setAttribute('data-merchantRedirect', checkoutData.redirectUrl);
    }
    if (checkoutData.webhookUrl) {
      script.setAttribute('data-serverWebhook', checkoutData.webhookUrl);
    }
    if (checkoutData.description) {
      script.setAttribute('data-description', checkoutData.description);
    }
    if (KASHIER_CONFIG.PAYMENT_METHODS.length > 0) {
      script.setAttribute('data-allowedMethods', KASHIER_CONFIG.PAYMENT_METHODS.join(','));
    }
    
    // Customer data for card saving (optional)
    if (checkoutData.customerEmail) {
      const customerData = JSON.stringify({
        email: checkoutData.customerEmail,
        name: checkoutData.customerName || ''
      });
      script.setAttribute('data-customer', customerData);
    }

    // Interaction source for e-commerce
    script.setAttribute('data-interactionSource', 'Ecommerce');
    
    // Enable 3DS
    script.setAttribute('data-enable3DS', 'true');

    console.log('✅ Kashier script attributes set:', {
      amount: checkoutData.amount,
      orderId: checkoutData.orderId,
      merchantId: checkoutData.merchantId,
      hash: checkoutData.hash,
      mode: KASHIER_CONFIG.TEST_MODE ? 'test' : 'live',
      type: 'external'
    });

    // Add event listeners for script loading
    script.onload = () => {
      console.log('✅ Kashier iframe script loaded');
      // Check for global Kashier object and try manual trigger
      setTimeout(() => {
        console.log('Checking window for Kashier objects:', Object.keys(window).filter(k => k.toLowerCase().includes('kashier')));
        if ((window as any).KashierCheckout) {
          console.log('🔔 Found KashierCheckout object');
        }
      }, 300);
    };
    
    script.onerror = (error) => {
      console.error('❌ Failed to load Kashier iframe script:', error);
    };
    
    // Append script to body - this should trigger the iframe modal automatically
    document.body.appendChild(script);
    
    console.log('📝 Kashier iframe script injected into body - modal should auto-trigger...');
  },
};